import { initializeBrowserPulse } from '@pulse/typescript';
import ReactGA4 from 'react-ga4';
import { GA4 } from 'react-ga4/types/ga4';
import { hotjar } from 'react-hotjar';

import { isDevelopment } from '../../utils/env';

import {
  IAnalytics,
  IG4DefaultParams,
  INavigationClickParams,
  IPopupActionParams,
  IPopupOpenParams,
  IToolSelectParams,
  ICategorySelectParams,
  ICategorySwitchParams,
  ISectionLinkClick,
  IStickyBannerClick,
  IPageClick,
} from './types';

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    dataLayer?: any[];
  }
}

class Analytics {
  protected ReactGA4: GA4;

  constructor(public options: IAnalytics) {
    this.ReactGA4 = ReactGA4;
  }

  get analyticsEnabled() {
    return !isDevelopment() && !!window;
  }

  public init() {
    this.initGA4();
    this.initPulse();
  }

  private initPulse() {
    if (!this.analyticsEnabled) {
      return;
    }

    console.log('[Pulse] init');

    initializeBrowserPulse({
      sdks: ['settings', 'tracker'],
      app: 'tools.picsart.com',
      config: {
        settings: {
          defaults: {},
        },
      },
    });
  }

  public initGA4() {
    if (!this.analyticsEnabled) {
      return;
    }

    console.log('[Analytics GA4] init');

    // The GA script is now initialized in SingleToolThirdParty
    // const gaTags = trackingIds.map((tag) => ({ trackingId: tag }));
    // ReactGA4.initialize(gaTags);
    ReactGA4.set({ appVersion: this.options.VERSION });
    ReactGA4.send('pageview');
  }

  public gaEvent(action: string, params: IG4DefaultParams) {
    if (!this.analyticsEnabled) {
      // console.log('[Analytics]', action, {
      //   ...params,
      //   tool_name: this.options.TOOL_NAME,
      // });
      return;
    }

    const tool_name = this.options.TOOL_NAME;

    params = {
      ...params,
      tool_name,
    };

    // Send event to GA
    ReactGA4.event(action, params);
    this.pulseEvent(action, params);
  }

  private pulseEvent(name: string, params: any) {
    window.dataLayer.push({
      pulse: 'event',
      event: name,
      parameters: params,
    });
  }

  public hjEvent(event: string) {
    if (!this.analyticsEnabled) {
      return;
    }

    if ('hj' in window) {
      hotjar.event(event);
    }
  }

  public hjStateChange(state: string) {
    if (!this.analyticsEnabled) {
      return;
    }

    if ('hj' in window) {
      hotjar.stateChange(state);
    }
  }

  public popupOpen(params: IPopupOpenParams) {
    this.gaEvent('popup_open', params);
    this.hjEvent('popup_open');
  }

  public popupAction(params: IPopupActionParams) {
    this.gaEvent('popup_action', params);
    this.hjEvent('popup_action');
  }

  public shareOpen(pageName: string) {
    this.gaEvent('share_open', {
      page_name: pageName,
    });
    this.hjEvent('share_open');
  }

  public sharePage(actionType: string, pageName: string) {
    this.gaEvent('page_share', {
      page_name: pageName,
      action_type: actionType,
    });
    this.hjEvent('page_share');
  }

  public footerLinkClicked(action_type: string, pageName = 'landing_page') {
    this.gaEvent('footer_click', {
      page_name: pageName,
      tool_name: this.options.TOOL_NAME,
      action_type: action_type,
    });
    this.hjEvent('footer_click');
  }

  public navigationClick(params: INavigationClickParams) {
    this.gaEvent('navigation_click', { ...params });
  }

  public searchBarClick(page_name = 'landing_page', tool_name: string) {
    this.gaEvent('search_bar_click', {
      page_name,
      tool_name,
    });
  }

  public searchBarType(page_name = 'landing_page', tool_name: string) {
    this.gaEvent('search_bar_type', {
      page_name,
      tool_name,
    });
  }

  public searchResultsClick(page_name = 'landing_page', tool_name: string, action_type: string) {
    this.gaEvent('search_results_click', {
      page_name,
      tool_name,
      action_type,
    });
  }

  public toolSelect(params: IToolSelectParams) {
    this.gaEvent('tool_select', { ...params });
  }

  public toolCategorySelect(params: ICategorySelectParams) {
    this.gaEvent('tool_category_select', { ...params });
  }

  public toolCategorySwitch(params: ICategorySwitchParams) {
    this.gaEvent('tool_category_switch', { ...params });
  }

  public moreTools(page_name: string) {
    this.gaEvent('more_tools_load', {
      page_name,
    });
  }

  public pageClick(params: IPageClick) {
    this.gaEvent('page_click', {
      ...params,
    });
  }

  public topPageAnchor(pageName: string) {
    this.gaEvent('top_page_anchor', { page_name: pageName });
  }

  public sectionLinkClick(params: ISectionLinkClick) {
    this.gaEvent('section_link_click', { ...params });
  }

  public stickyBannerClick(params: IStickyBannerClick) {
    this.gaEvent('sticky_banner_click', { ...params });
  }
}

export default Analytics;
